import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Newspaper, ExternalLink, ChevronRight, Sparkles, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const SENTIMENT_STYLE = {
  BULLISH: { icon: TrendingUp, cls: "text-emerald-400" },
  BEARISH: { icon: TrendingDown, cls: "text-red-400" },
  NEUTRAL: { icon: Minus, cls: "text-gray-400" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

/**
 * Compact news strip for Dashboard — shows top 5 articles touching user's tickers
 * with AI takeaway. Links to full /news page.
 */
export default function NewsStrip() {
  const [items, setItems] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      // Pull top articles from the "my_stocks" filter so strip is always personal
      const res = await api.get("/news/feed?filter=my_stocks&limit=20");
      setItems(res.data?.articles || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, 180000); // 3 min
    return () => clearInterval(t);
  }, [fetchData]);

  if (items === null) {
    return (
      <Card className="card-dark border-cyan-500/20 bg-cyan-500/5">
        <CardContent className="p-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Newspaper className="w-4 h-4 text-cyan-400 animate-pulse" />
          Loading news for your tickers…
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return null;  // don't show empty strip; full /news page is still available
  }

  const top = items.slice(0, 5);

  return (
    <Card className="card-dark border-cyan-500/20 bg-cyan-500/5" data-testid="news-strip">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-xs font-bold text-cyan-400">
            <Newspaper className="w-4 h-4" />
            NEWS ON YOUR STOCKS
            <span className="text-gray-500 font-normal">({items.length})</span>
          </div>
          <Link
            to="/news"
            className="text-xs text-cyan-400 hover:text-cyan-300 font-bold flex items-center gap-1"
            data-testid="news-strip-viewall"
          >
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="space-y-2">
          {top.map((a) => {
            const sentiment = SENTIMENT_STYLE[a.ai_sentiment] || SENTIMENT_STYLE.NEUTRAL;
            const SentimentIcon = sentiment.icon;
            return (
              <a
                key={a.article_id}
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`news-strip-item-${a.article_id}`}
                className="flex items-start gap-3 p-2 rounded-sm hover:bg-white/5 transition-colors"
              >
                <SentimentIcon className={`w-4 h-4 ${sentiment.cls} flex-shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-white/20 text-gray-400">
                      {a.source_display}
                    </Badge>
                    {(a.tickers_mentioned || []).slice(0, 3).map((t) => (
                      <span
                        key={t}
                        className="text-[10px] font-bold px-1 py-0 rounded-sm bg-purple-500/15 text-purple-300"
                      >
                        {t}
                      </span>
                    ))}
                    <span className="text-[10px] text-gray-500 ml-auto">{timeAgo(a.published_at)}</span>
                  </div>
                  <p className="text-sm text-white leading-snug line-clamp-1">{a.title}</p>
                  {a.ai_takeaway && (
                    <p className="text-xs text-cyan-200/80 leading-snug line-clamp-1 flex items-start gap-1 mt-0.5">
                      <Sparkles className="w-3 h-3 text-cyan-400 flex-shrink-0 mt-0.5" />
                      {a.ai_takeaway}
                    </p>
                  )}
                </div>
                <ExternalLink className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-1" />
              </a>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
